import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { readAnalysisContext } from '../astra/context';
import { defineMessages } from '../../i18n/define';
import { UNIT_LABELS, useT } from '../../i18n/hooks';
import { messageRecord } from '../lib/messageRecord';
import { useTrack } from '../data/TrackContext';
import { PageHeader } from '../ui/PageHeader';
import { Card, Grid, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { DoneSplit } from '../ui/DoneSplit';
import { Disclosure } from '../ui/Disclosure';
import { DataTable, type Column } from '../ui/DataTable';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { MapWidget } from '../map/MapWidget';
import { DataGate } from './parts';
import { SkeletonList } from '../ui/Skeleton';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import type { DataState } from '../data/useTrackData';
import { FILES } from '../data/loader';
import { useAnalysisContext } from '../../analysis/context';
import {
  EPISTEMIC_WORD,
  farmLandSet,
  scopeProgressSet,
  worstEpistemic,
  type ScopeProgressFile,
} from '../data-source/api/cards';
import {
  countOf,
  cycleNameFromId,
  cycleTitle,
  dateShort,
  endDot,
  ha,
  haUnit,
  m3,
  num,
  placementUnknown,
  WORDS,
} from '../lib/format';
import { DENSITY } from '../lib/levels';
import { useRouteSource } from '../data-source/provider';
import { FarmCabinet } from './cabinet/FarmCabinet';
import {
  extraOutsideNote,
  ORIGIN_BADGE_LABEL,
  ORIGIN_DETAIL_LABEL,
  ORIGIN_ICON,
  farmExtraWorks,
  originSummaryLine,
  requirementHref,
  requirementLine,
  useExtraWorksBothCycles,
  type ExtraWork,
} from './my/extraWorks';
import type { ContourMeta, Crop, Cycle, Farm, Massif } from '../data/types';
import './pages.css';

const m = defineMessages({
  catArableIrr: { id: 'farm.detail.category.arableIrr', defaultMessage: 'пашня орошаемая', description: 'Категория землепользования в разбивке на карточке хозяйства (учебные данные): орошаемая пашня; строчными' },
  catArableRain: { id: 'farm.detail.category.arableRain', defaultMessage: 'пашня богарная', description: 'Категория землепользования: богарная (неорошаемая) пашня; строчными' },
  catOrchard: { id: 'farm.detail.category.orchard', defaultMessage: 'сад', description: 'Категория землепользования: сад; строчными' },
  catVineyard: { id: 'farm.detail.category.vineyard', defaultMessage: 'виноградник', description: 'Категория землепользования: виноградник; строчными' },
  catPasture: { id: 'farm.detail.category.pasture', defaultMessage: 'пастбище', description: 'Категория землепользования: пастбище; строчными' },
  catOther: { id: 'farm.detail.category.other', defaultMessage: 'прочее', description: 'Категория землепользования: прочее; строчными' },
  catUnknown: { id: 'farm.detail.category.unknown', defaultMessage: 'вид права не указан', description: 'Категория землепользования, когда вид права в данных не назван (незнание словами, не «unknown»); строчными' },
  levelDistrict: { id: 'farm.detail.level.district', defaultMessage: 'Район', description: 'Уровень в хлебных крошках карточки хозяйства (учебные данные): район' },
  districtName: { id: 'farm.detail.districtName', defaultMessage: 'Бука', description: 'Название района в хлебных крошках (Букинский район); имя собственное' },
  levelMassif: { id: 'farm.detail.level.massif', defaultMessage: 'Массив', description: 'Уровень в хлебных крошках карточки хозяйства: массив' },
  levelFarm: { id: 'farm.detail.level.farm', defaultMessage: 'Хозяйство', description: 'Уровень в хлебных крошках карточки хозяйства: хозяйство' },
  titleFallback: { id: 'farm.detail.titleFallback', defaultMessage: 'Хозяйство {id}', description: 'Заголовок страницы хозяйства, когда имя не найдено; {id} — идентификатор в адресе' },
  purposeFound: { id: 'farm.detail.purpose.found', defaultMessage: '{head}Величины считаются в контурах, работах на полях и гектарах этого хозяйства — доли в районе здесь нет, у хозяйства свой знаменатель.', description: 'Подзаголовок карточки хозяйства; {head} — готовый префикс: направление и руководитель, может быть пустым' },
  purposeHead: { id: 'farm.detail.purpose.head', defaultMessage: 'Руководитель: {name} ', description: 'Часть подзаголовка карточки хозяйства; {name} — ФИО руководителя (из данных) с точкой в конце' },
  purposeMissing: { id: 'farm.detail.purpose.missing', defaultMessage: 'Хозяйство в срезе не найдено — проверьте адрес или трек данных.', description: 'Подзаголовок страницы хозяйства, когда его нет в срезе' },
  kpiLand: { id: 'farm.detail.kpi.land', defaultMessage: 'Земля хозяйства', description: 'Подпись KPI-карточки: общая земля хозяйства, га' },
  kpiLandNote: { id: 'farm.detail.kpi.landNote', defaultMessage: 'общая площадь землепользования · {contours} по реестру', description: 'Нижняя строка KPI «Земля хозяйства»; {contours} — «12 контуров»' },
  kpiLandFail: { id: 'farm.detail.kpi.landFail', defaultMessage: 'реестр хозяйств не прочитан', description: 'Нижняя строка KPI «Земля хозяйства», когда реестр не прочитан' },
  kpiContours: { id: 'farm.detail.kpi.contours', defaultMessage: 'Контуры', description: 'Подпись KPI-карточки: число контуров хозяйства; коротко' },
  kpiContoursNote: { id: 'farm.detail.kpi.contoursNote', defaultMessage: '{ha} га общей площади · все землепользования хозяйства, включая контуры, которые оно делит с другими', description: 'Нижняя строка KPI «Контуры»; {ha} — гектары строкой' },
  kpiContoursFail: { id: 'farm.detail.kpi.contoursFail', defaultMessage: 'землепользования хозяйства не прочитаны', description: 'Нижняя строка KPI «Контуры», когда землепользования не прочитаны' },
  kpiWorks: { id: 'farm.detail.kpi.works', defaultMessage: 'Работы на полях по агроциклам', description: 'Подпись KPI-карточки: сколько работ на полях у хозяйства во всех агроциклах' },
  kpiLoading: { id: 'farm.detail.kpi.loading', defaultMessage: 'ещё загружаются', description: 'Нижняя строка KPI, пока данные грузятся' },
  kpiProgressFail: { id: 'farm.detail.kpi.progressFail', defaultMessage: 'ход агроциклов хозяйства не получен', description: 'Нижняя строка KPI, когда ход агроциклов хозяйства не получен' },
  kpiWorksNote: { id: 'farm.detail.kpi.worksNote', defaultMessage: 'по агроциклам, где участвует хозяйство', description: 'Нижняя строка KPI «Работы на полях по агроциклам»' },
  kpiOverdue: { id: 'farm.detail.kpi.overdue', defaultMessage: 'После срока', description: 'Подпись KPI-карточки: работы на полях после срока без отчёта; коротко' },
  kpiOverdueNoRows: { id: 'farm.detail.kpi.overdueNoRows', defaultMessage: 'строк, по которым считают молчание, у этих агроциклов не было вовсе', description: 'Нижняя строка KPI «После срока»: считать было не из чего' },
  kpiOverdueNotMeasured: { id: 'farm.detail.kpi.overdueNotMeasured', defaultMessage: 'сервер не измерил молчание по этим агроциклам — «нет данных», а не ноль', description: 'Нижняя строка KPI «После срока»: сервер не измерил' },
  kpiOverdueNone: { id: 'farm.detail.kpi.overdueNone', defaultMessage: 'работ на полях не назначали — считать нечего', description: 'Нижняя строка KPI «После срока», когда работ нет' },
  kpiOverdueNoneClass: { id: 'farm.detail.kpi.overdueNoneClass', defaultMessage: 'работ на полях не назначали — считать нечего · {epistemic}', description: 'Нижняя строка KPI «После срока», когда работ нет; {epistemic} — класс доказательства (Факт, Синтетика…)' },
  kpiOverdueOf: { id: 'farm.detail.kpi.overdueOf', defaultMessage: 'из {works} — без отчёта после срока', description: 'Нижняя строка KPI «После срока»; {works} — «12 работ на полях»' },
  kpiOverdueOfClass: { id: 'farm.detail.kpi.overdueOfClass', defaultMessage: 'из {works} — без отчёта после срока · {epistemic}', description: 'Нижняя строка KPI «После срока»; {works} — «12 работ на полях», {epistemic} — класс доказательства' },
  cyclesTitle: { id: 'farm.detail.cycles.title', defaultMessage: 'Хозяйство по агроциклам', description: 'Заголовок карточки с полосами выполнения по агроциклам хозяйства' },
  cyclesLoading: { id: 'farm.detail.cycles.loading', defaultMessage: 'работы на полях по агроциклам', description: 'Что грузится (читает диктор «Загружаю …»): работы на полях по агроциклам' },
  denominator: { id: 'farm.detail.cycles.denominator', defaultMessage: '{count, plural, one {работа на поле хозяйства} few {работы на полях хозяйства} many {работ на полях хозяйства} other {работы на полях хозяйства}}', description: 'Знаменатель под полосой выполнения; слово без числа, согласуется с {count}' },
  denominatorCycle: { id: 'farm.detail.cycles.denominatorCycle', defaultMessage: '{count, plural, one {работа на поле хозяйства в агроцикле} few {работы на полях хозяйства в агроцикле} many {работ на полях хозяйства в агроцикле} other {работы на полях хозяйства в агроцикле}}', description: 'Знаменатель под полосой выполнения по агроциклу; слово без числа, согласуется с {count}' },
  cyclesMissing: { id: 'farm.detail.cycles.missing', defaultMessage: 'ход агроциклов хозяйства не получен: {reason}', description: 'Пояснение под пустой полосой выполнения; {reason} — причина от сервера' },
  noReason: { id: 'farm.detail.cycles.noReason', defaultMessage: 'причина не названа', description: 'Причина, когда сервер её не назвал; встаёт после двоеточия, строчными' },
  cyclesNoneDemo: { id: 'farm.detail.cycles.noneDemo', defaultMessage: 'работ на полях не назначали: ни один агроцикл среза не завёл работу на контуры этого хозяйства', description: 'Пояснение под пустой полосой выполнения (демо включено)' },
  cyclesNoneNoDemo: { id: 'farm.detail.cycles.noneNoDemo', defaultMessage: 'в срезе без демо-данных работ на полях у хозяйства нет; синтетические кампании пилота учитываются только при включённом «Показывать демо-данные»', description: 'Пояснение под пустой полосой выполнения (демо выключено); «Показывать демо-данные» — название переключателя в интерфейсе' },
  cycleActive: { id: 'farm.detail.cycles.active', defaultMessage: 'идёт', description: 'Бейдж агроцикла: идёт сейчас; коротко' },
  cycleClosed: { id: 'farm.detail.cycles.closed', defaultMessage: 'закрыт', description: 'Бейдж агроцикла: закрыт; коротко' },
  windowNote: { id: 'farm.detail.cycles.windowNote', defaultMessage: 'часть окон ещё впереди — молчание не равно невыполнению.', description: 'Пояснение под полосой выполнения идущего агроцикла; строчными (встаёт как предложение)' },
  voluntaryTitle: { id: 'farm.detail.voluntary.title', defaultMessage: 'Добровольные работы', description: 'Заголовок карточки добровольных работ хозяйства (работы вне плана агроцикла)' },
  voluntaryGate: { id: 'farm.detail.voluntary.gate', defaultMessage: 'блок добровольных работ ждёт файлы extra-works-*.json', description: 'Сообщение, когда не найдены файлы учебных данных; extra-works-*.json — шаблон имён файлов, не переводить' },
  voluntaryList: { id: 'farm.detail.voluntary.list', defaultMessage: 'Список добровольных работ', description: 'Заголовок раскрывающегося списка добровольных работ' },
  voluntaryNone: { id: 'farm.detail.voluntary.none', defaultMessage: 'добровольных работ у этого хозяйства в срезе нет', description: 'Краткое пояснение у списка добровольных работ, когда их нет' },
  voluntarySummary: { id: 'farm.detail.voluntary.summary', defaultMessage: '{works} · {origins}', description: 'Краткое пояснение у списка добровольных работ; {works} — «3 работы», {origins} — откуда они взялись' },
  voluntaryEmptyTitle: { id: 'farm.detail.voluntary.emptyTitle', defaultMessage: 'Записей нет', description: 'Заголовок пустой таблицы добровольных работ' },
  voluntaryEmptyReason: { id: 'farm.detail.voluntary.emptyReason', defaultMessage: 'добровольных работ этого хозяйства в срезе нет', description: 'Пояснение пустой таблицы добровольных работ' },
  rowWord: { id: 'farm.detail.rowWord', defaultMessage: '{count, plural, one {строка} few {строки} many {строк} other {строки}}', description: 'Слово «строка» без числа — знаменатель под таблицей добровольных работ' },
  colDate: { id: 'farm.detail.col.date', defaultMessage: 'Дата', description: 'Колонка таблицы добровольных работ: дата' },
  colCycle: { id: 'farm.detail.col.cycle', defaultMessage: 'Агроцикл', description: 'Колонка таблицы добровольных работ: агроцикл' },
  colContour: { id: 'farm.detail.col.contour', defaultMessage: 'Контур', description: 'Колонка таблицы добровольных работ: контур (номер — ссылка)' },
  contourNo: { id: 'farm.detail.contourNo', defaultMessage: '№ {no}', description: 'Ссылка на контур в таблице: номер контура' },
  contourRef: { id: 'farm.detail.contourRef', defaultMessage: 'контур #{id}', description: 'Ссылка на контур, когда номера нет; {id} — внутренний идентификатор' },
  colWork: { id: 'farm.detail.col.work', defaultMessage: 'Работа', description: 'Колонка таблицы добровольных работ: работа' },
  colOrigin: { id: 'farm.detail.col.origin', defaultMessage: 'Происхождение', description: 'Колонка таблицы добровольных работ: откуда взялась работа (вручную, из находки карантина, полив вне плана)' },
  contoursSummary: { id: 'farm.detail.contours.summary', defaultMessage: '{count} — {ha} га', description: 'Краткое пояснение у раскрывающегося списка «Контуры»; {count} — число контуров, {ha} — площадь строкой' },
  contoursLabel: { id: 'farm.detail.contours.label', defaultMessage: 'Контуры', description: 'Заголовок раскрывающегося списка контуров хозяйства' },
  contoursGate: { id: 'farm.detail.contours.gate', defaultMessage: 'таблица контуров ждёт землепользования хозяйства', description: 'Сообщение, когда землепользования хозяйства не прочитаны' },
  colNo: { id: 'farm.detail.col.no', defaultMessage: 'Номер', description: 'Колонка таблицы контуров хозяйства: номер контура (ссылка)' },
  colArea: { id: 'farm.detail.col.area', defaultMessage: 'Площадь', description: 'Колонка таблицы контуров хозяйства: площадь, га' },
  colCategory: { id: 'farm.detail.col.category', defaultMessage: 'Категория', description: 'Колонка таблицы контуров хозяйства: категория земли' },
  colCrop: { id: 'farm.detail.col.crop', defaultMessage: 'Культура 2026', description: 'Колонка таблицы контуров хозяйства: культура сезона 2026' },
  contourWord: { id: 'farm.detail.contourWord', defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}', description: 'Слово «контур» без числа — знаменатель под таблицей контуров' },
  contoursEmptyTitle: { id: 'farm.detail.contours.emptyTitle', defaultMessage: 'Контуров нет', description: 'Заголовок пустой таблицы контуров хозяйства' },
  contoursEmptyReason: { id: 'farm.detail.contours.emptyReason', defaultMessage: 'у хозяйства в каноне нет ни одного землепользования', description: 'Пояснение пустой таблицы контуров хозяйства' },
  waterTitle: { id: 'farm.detail.water.title', defaultMessage: 'Вода хозяйства', description: 'Заголовок карточки воды хозяйства' },
  waterLoading: { id: 'farm.detail.water.loading', defaultMessage: 'данные по воде', description: 'Что грузится (читает диктор «Загружаю …»): данные по воде' },
  waterNoneTitle: { id: 'farm.detail.water.noneTitle', defaultMessage: 'Данных по воде нет', description: 'Заголовок пустого состояния карточки воды хозяйства' },
  waterMissing: { id: 'farm.detail.water.missing', defaultMessage: 'баланс воды хозяйства не получен: {reason}', description: 'Пояснение пустого состояния воды; {reason} — причина от сервера' },
  waterAbsent: { id: 'farm.detail.water.absent', defaultMessage: 'баланс воды хозяйства в ответе не приехал', description: 'Пояснение пустого состояния воды: сервер не вернул блок воды' },
  waterRequests: { id: 'farm.detail.water.requests', defaultMessage: 'Заявок хозяйства', description: 'Строка баланса воды: число заявок хозяйства' },
  waterRequested: { id: 'farm.detail.water.requested', defaultMessage: 'Запрошено', description: 'Строка баланса воды: запрошенный объём' },
  waterDelivered: { id: 'farm.detail.water.delivered', defaultMessage: 'Подано', description: 'Строка баланса воды: поданный объём' },
  waterNote: { id: 'farm.detail.water.note', defaultMessage: 'Баланс посчитан по хозяйству целиком, а не по отдельному агроциклу: у заявки на воду метки сезона нет, и разложить её по агроциклам нечем.', description: 'Пояснение под балансом воды хозяйства' },
  mapTitle: { id: 'farm.detail.map.title', defaultMessage: 'Земля хозяйства на карте', description: 'Заголовок карточки с картой земли хозяйства' },
  mapCaption: { id: 'farm.detail.map.caption', defaultMessage: 'Только контуры этого хозяйства', description: 'Подпись под картой хозяйства' },
  usageTitle: { id: 'farm.detail.usage.title', defaultMessage: 'Землепользование', description: 'Заголовок блока справа от карты: разбивка земли хозяйства по категориям' },
  usageNone: { id: 'farm.detail.usage.none', defaultMessage: 'У хозяйства нет ни одного землепользования — разбивать по категориям нечего.', description: 'Пустое состояние блока «Землепользование»' },
  usageNotRead: { id: 'farm.detail.usage.notRead', defaultMessage: 'Землепользования не прочитаны: разбивку по категориям показать нечем.', description: 'Пустое состояние блока «Землепользование», когда данные не прочитаны' },
  usageRow: { id: 'farm.detail.usage.row', defaultMessage: '{label} · {contours}', description: 'Строка разбивки: {label} — категория, {contours} — «5 контуров»' },
  placementTitle: { id: 'farm.detail.placement.title', defaultMessage: 'Размещение и геометрия', description: 'Заголовок блока справа от карты: сколько контуров с культурой и геометрией' },
  placementNote: { id: 'farm.detail.placement.note', defaultMessage: 'Культура 2026 проставлена у {placed} из {contours} хозяйства; {geometry}. Контур без полигона на кадре не показан — это пробел геометрии, а не потерянная земля.', description: 'Пояснение в блоке «Размещение и геометрия»; {placed} — число, {contours} — «12 контуров» (родительный), {geometry} — фраза про полигоны' },
  geometryUnknown: { id: 'farm.detail.placement.geometryUnknown', defaultMessage: 'наличие полигона землепользование не называет', description: 'Часть пояснения про геометрию: данные не называют, есть ли полигон' },
  geometryHave: { id: 'farm.detail.placement.geometryHave', defaultMessage: 'полигон есть у {count}', description: 'Часть пояснения про геометрию; {count} — число контуров с полигоном' },
  placementLoading: { id: 'farm.detail.placement.loading', defaultMessage: 'Пока реестр не прочитан, счёт размещений и полигонов не выводится.', description: 'Пояснение в блоке «Размещение и геометрия», пока реестр не прочитан' },
  contoursGenitive: { id: 'farm.detail.contoursGenitive', defaultMessage: '{count, plural, one {# контура} few {# контуров} many {# контуров} other {# контура}}', description: 'Число контуров в родительном падеже («из 5 контуров»), встаёт после «из»' },
  haValue: { id: 'farm.detail.haValue', defaultMessage: '{ha} га', description: 'Гектары с единицей; {ha} — число строкой' },
});

const CATEGORY_LABEL: Record<string, string> = messageRecord<string>({
  arable_irr: m.catArableIrr,
  arable_rain: m.catArableRain,
  orchard: m.catOrchard,
  vineyard: m.catVineyard,
  pasture: m.catPasture,
  other: m.catOther,
  // Канон вид права/категорию землепользования не называет: незнание словами, не «unknown».
  unknown: m.catUnknown,
});

/**
 * Ход циклов и вода хозяйства — ГОТОВЫЕ агрегаты сервера по срезу
 * «цикл × хозяйство» (`data-source/api/cards.ts`). Раньше экран читал журнал
 * обязательств и журнал воды целиком и складывал их в браузере по `farm_id`;
 * канон таких журналов не отдаёт — считает сервер. Пара зашитых циклов
 * прототипа тоже ушла: ключ цикла канона — пара «метка сезона × культура», и
 * зашитое имя не сошлось бы ни с одной строкой среза.
 */
interface CycleAgg {
  total: number;
  reported: number;
  verified: number;
  /** Не измерено — `undefined`; ноль здесь означал бы «все отчитались». */
  silent: number | undefined;
}

/**
 * Сумма измеренного. Если хотя бы одно слагаемое не измерено, суммы НЕТ:
 * сложить измеренное с неизмеренным и назвать это итогом значило бы выдать
 * часть за целое.
 */
function sumMeasured(values: readonly (number | undefined)[]): number | undefined {
  if (values.length === 0 || values.some((value) => value === undefined)) return undefined;
  return values.reduce<number>((sum, value) => sum + (value as number), 0);
}

function anyLoading(...resources: { state: DataState }[]): boolean {
  return resources.some((r) => r.state === 'loading');
}

/**
 * Карточка хозяйства выбирает реализацию по источнику маршрута: на API —
 * кабинет хозяйства (`cabinet/FarmCabinet`, agroai-6al.34), иначе —
 * фикстурный паспорт прототипа. Id двух миров не пересекаются.
 */
export function FarmDetail() {
  const { id = '' } = useParams();
  const canon = useRouteSource()?.source === 'api' && /^\d+$/.test(id);
  return canon ? <FarmCabinet farmId={Number(id)} /> : <FarmDetailFixtures />;
}

export function FarmDetailFixtures() {
  const { id = '' } = useParams();
  const t = useT();
  const { track } = useTrack();
  const { includeDemo } = useAnalysisContext();
  // Срез картины сезона — тот, что выбран на «/» (память среза), плюс это
  // хозяйство. Ни цикл, ни дата не зашиваются: зашитые «cycle-cotton-2026» и
  // «26 августа» прототипа на каноне не существуют и уводили на чужой срез.
  const analysisContext = readAnalysisContext(track);
  analysisContext.set('farm', id);
  analysisContext.delete('massif');
  if (includeDemo) analysisContext.delete('include_demo');
  else analysisContext.set('include_demo', 'false');
  const farms = useTrackData<Farm[]>(FILES.farms);
  // Земля хозяйства — ВСЕ его землепользования (`/actors/{id}`), как в
  // кабинете хозяйства, а не только контуры, где оно названо первым
  // землепользователем реестра (см. `api/cards.ts`).
  const contours = useTrackData<ContourMeta[]>(farmLandSet(id));
  const crops = useTrackData<Crop[]>(FILES.crops);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const progress = useTrackData<ScopeProgressFile>(scopeProgressSet('farm', id));
  const extraBundle = useExtraWorksBothCycles();
  const farm = (farms.data ?? []).find((f) => f.id === id) ?? null;
  const massifId = farm?.massif_ids[0] ?? null;
  const massifName = massifId ? massifs.data?.find((m) => m.id === massifId)?.name ?? massifId : null;

  const own = useMemo(
    () => contours.data ?? [],
    [contours.data],
  );
  const ownIds = useMemo(() => own.map((c) => c.id), [own]);
  const ownArea = useMemo(() => own.reduce((s, c) => s + c.area_ha, 0), [own]);
  const ownContourById = useMemo(() => new Map(own.map((c) => [c.id, c])), [own]);

  /** Спутник кадра карты: землепользование хозяйства по убыванию площади. */
  const byCategory = useMemo(() => {
    const agg = new Map<string, { key: string; label: string; contours: number; area: number }>();
    for (const row of own) {
      const key = row.category;
      const hit = agg.get(key) ?? {
        key,
        label: CATEGORY_LABEL[key] ?? key,
        contours: 0,
        area: 0,
      };
      hit.contours += 1;
      hit.area += row.area_ha;
      agg.set(key, hit);
    }
    return [...agg.values()].sort((a, b) => b.area - a.area);
  }, [own]);

  const placed = useMemo(() => own.filter((c) => c.crop_2026 !== null).length, [own]);
  const withGeometry = useMemo(() => own.filter((c) => c.geometry === 'accepted').length, [own]);
  /** Землепользование наличия полигона не называет — счёт полигонов не выводится. */
  const geometryUnknown = useMemo(() => own.length > 0 && own.every((c) => c.geometry === 'unknown'), [own]);

  const cropNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of crops.data ?? []) map.set(c.id, c.name);
    return map;
  }, [crops.data]);
  const cropName = (cropId: string | null) =>
    cropId ? cropNameById.get(cropId) ?? cropId : placementUnknown();

  usePageTrail([
    { level: t(m.levelDistrict), name: t(m.districtName), to: '/' },
    { level: t(m.levelMassif), name: massifName ?? massifId ?? '—', to: massifId ? `/farms?massif=${massifId}` : undefined },
    { level: t(m.levelFarm), name: farm?.name ?? id },
  ]);

  const cycleById = useMemo(() => new Map((cycles.data ?? []).map((c) => [c.id, c])), [cycles.data]);

  /**
   * Циклы хозяйства: строка хода сервера, склеенная с карточкой цикла среза.
   * Цикл без обязательств в карточку не попадает — так было и на прототипе.
   */
  const farmCycles = useMemo(() => {
    const entries: { cycle: Cycle; agg: CycleAgg }[] = [];
    for (const row of progress.data?.cycles ?? []) {
      const cycle = cycleById.get(row.cycle);
      if (!cycle || row.obligations.total === 0) continue;
      entries.push({
        cycle,
        agg: {
          total: row.obligations.total,
          reported: row.obligations.reported,
          verified: row.obligations.verified,
          silent: row.obligations.silent,
        },
      });
    }
    return entries;
  }, [cycleById, progress.data]);

  const obligationsLoading = anyLoading(cycles, progress);
  const obligationsMissing = progress.state === 'missing';
  const totalObligations = (progress.data?.cycles ?? []).reduce(
    (sum, row) => sum + row.obligations.total,
    0,
  );
  const silentNow = sumMeasured((progress.data?.cycles ?? []).map((row) => row.obligations.silent));
  /**
   * Класс доказательства «молчащих» — наихудший среди циклов, как считает сам
   * сервер. Без него синтетика демонстрации печаталась бы на карточке
   * неотличимо от официального счёта.
   */
  const silentClass = worstEpistemic(
    (progress.data?.cycles ?? []).map((row) => row.obligations.silentEpistemic),
  );
  /** Строк, из которых считалось молчание. Ноль — считать было не из чего. */
  const silentRows = (progress.data?.cycles ?? []).reduce(
    (sum, row) => sum + row.obligations.silentRows,
    0,
  );

  /**
   * Вода хозяйства — ОДНО значение среза, а не слагаемое по циклам: канон
   * осями сезона и культуры водный баланс не режет (см. `api/cards.ts`), и
   * сложение поцикловых ответов умножило бы заявки на число циклов.
   */
  const waterLoading = progress.state === 'loading';
  const waterMissing = obligationsMissing;
  const waterTotal = progress.data?.water;
  /**
   * Оговорка сервера о подаче. На демонстрационном срезе именно она объясняет,
   * откуда взялась подача, поэтому печатается рядом с числами, а не прячется.
   */
  const waterNote = waterTotal?.note ?? '';

  const extraColumns: Column<ExtraWork>[] = [
    { key: 'date', header: t(m.colDate), width: '130px', render: (row) => dateShort(row.date) },
    {
      key: 'cycle',
      header: t(m.colCycle),
      render: (row) => {
        const cycle = cycleById.get(row.cycle_id);
        return cycle ? cycleTitle(cycle, crops.data) : cycleNameFromId(row.cycle_id, crops.data);
      },
    },
    {
      key: 'contour',
      header: t(m.colContour),
      render: (row) => {
        const contour = ownContourById.get(row.contour_id);
        return <Link to={`/contours/${row.contour_id}`}>{contour?.no ? t(m.contourNo, { no: contour.no }) : t(m.contourRef, { id: row.contour_id })}</Link>;
      },
    },
    {
      key: 'name',
      header: t(m.colWork),
      render: (row) => {
        const requirement = requirementLine(row);
        return (
          <>
            {row.name}
            {row.note ? <div className="t-caption">{row.note}</div> : null}
            {requirement ? (
              <div className="t-caption">
                <Link to={requirementHref(row)}>{requirement}</Link>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'origin',
      header: t(m.colOrigin),
      render: (row) => (
        <>
          <Badge tone="copper" icon={ORIGIN_ICON[row.origin]}>
            {ORIGIN_BADGE_LABEL[row.origin]}
          </Badge>
          <div className="t-caption">{ORIGIN_DETAIL_LABEL[row.origin]}</div>
        </>
      ),
    },
  ];

  /** Дата — первая колонка, значит и порядок по ней: свежее сверху. */
  const extraRowsByDate = (rows: ExtraWork[]): ExtraWork[] =>
    [...rows].sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id));

  const contourColumns: Column<ContourMeta>[] = [
    { key: 'no', header: t(m.colNo), render: (row) => <Link to={`/contours/${row.id}`}>{t(m.contourNo, { no: row.no })}</Link> },
    { key: 'area', header: t(m.colArea), unit: t(UNIT_LABELS.ha), numeric: true, render: (row) => ha(row.area_ha) },
    {
      key: 'category',
      header: t(m.colCategory),
      render: (row) => CATEGORY_LABEL[row.category] ?? row.category,
    },
    { key: 'crop', header: t(m.colCrop), render: (row) => cropName(row.crop_2026) },
  ];

  return (
    <Stack>
      <PageHeader
        level="farm"
        title={farm?.name ?? t(m.titleFallback, { id })}
        purpose={
          farm
            ? // Инициал руководителя уже кончается точкой — второй не ставим.
              t(m.purposeFound, { head: `${farm.direction ? `${endDot(farm.direction)} ` : ''}${farm.head ? t(m.purposeHead, { name: endDot(farm.head) }) : ''}` })
            : t(m.purposeMissing)
        }
      />

      <Grid cols={4}>
        <KpiCard
          label={t(m.kpiLand)}
          value={farm ? ha(farm.area_ha) : '—'}
          unit={t(UNIT_LABELS.ha)}
          note={farm ? t(m.kpiLandNote, { contours: countOf(farm.contours, WORDS.contour) }) : t(m.kpiLandFail)}
        />
        <KpiCard
          label={t(m.kpiContours)}
          value={contours.state === 'ready' ? num(own.length) : '—'}
          note={
            contours.state === 'ready'
              ? t(m.kpiContoursNote, { ha: ha(ownArea) })
              : t(m.kpiContoursFail)
          }
        />
        <KpiCard
          label={t(m.kpiWorks)}
          value={obligationsLoading ? '—' : obligationsMissing ? '—' : num(totalObligations)}
          note={
            obligationsLoading
              ? t(m.kpiLoading)
              : obligationsMissing
                ? t(m.kpiProgressFail)
                : t(m.kpiWorksNote)
          }
        />
        {/*
          Ноль без обязательств и ноль при закрытых окнах — разные состояния.
          Первый — «нечего было считать» (No eligible), второй — молчание по
          назначенному. Одной формулировкой их называть нельзя.
        */}
        <KpiCard
          label={t(m.kpiOverdue)}
          value={
            obligationsLoading || obligationsMissing || silentNow === undefined
              ? '—'
              : num(silentNow)
          }
          note={
            obligationsLoading
              ? t(m.kpiLoading)
              : obligationsMissing
                ? t(m.kpiProgressFail)
                : silentNow === undefined
                  ? silentRows === 0
                    ? t(m.kpiOverdueNoRows)
                    : t(m.kpiOverdueNotMeasured)
                  : totalObligations === 0
                    ? silentClass
                      ? t(m.kpiOverdueNoneClass, { epistemic: EPISTEMIC_WORD[silentClass] })
                      : t(m.kpiOverdueNone)
                    : silentClass
                      ? t(m.kpiOverdueOfClass, { works: countOf(totalObligations, WORDS.obligation), epistemic: EPISTEMIC_WORD[silentClass] })
                      : t(m.kpiOverdueOf, { works: countOf(totalObligations, WORDS.obligation) })
          }
        />
      </Grid>


      <Card size="lg" title={t(m.cyclesTitle)}>
        {obligationsLoading ? (
          <SkeletonList label={t(m.cyclesLoading)} items={2} tone="soft" />
        ) : farmCycles.length === 0 ? (
          <DoneSplit
            counts={null}
            denominator={m.denominator}
            reason={
              obligationsMissing
                ? t(m.cyclesMissing, { reason: progress.reason ?? t(m.noReason) })
                : includeDemo
                  ? t(m.cyclesNoneDemo)
                  : t(m.cyclesNoneNoDemo)
            }
          />
        ) : (
          <Stack tight>
            {farmCycles.map(({ cycle, agg }) => (
              <div className="cyclecard" key={cycle.id}>
                <div className="cyclecard__row">
                  <span className="cyclecard__name">
                    <Link to={`/?${(() => { const q = new URLSearchParams(analysisContext); q.set('cycle', cycle.id); return q; })()}`}>{cycleTitle(cycle, crops.data)}</Link>
                  </span>
                  <Badge tone={cycle.status === 'active' ? 'sage' : 'outline'}>
                    {cycle.status === 'active' ? t(m.cycleActive) : t(m.cycleClosed)}
                  </Badge>
                </div>
                <DoneSplit
                  size="sm"
                  counts={{ total: agg.total, reported: agg.reported, verified: agg.verified }}
                  denominator={m.denominatorCycle}
                  windowNote={
                    cycle.status === 'active'
                      ? t(m.windowNote)
                      : undefined
                  }
                />
              </div>
            ))}
          </Stack>
        )}
      </Card>

      {/* Одно имя блока, а не два в одной карточке: «Сверх шаблона» снаружи и
          «Добровольные работы хозяйства» внутри читались как разные объекты. */}
      <Card title={t(m.voluntaryTitle)}>
        <DataGate res={extraBundle} what={t(m.voluntaryGate)}>
          {(rows) => {
            const mine = farmExtraWorks(rows, id);
            return (
              <Disclosure
                label={t(m.voluntaryList)}
                summary={
                  mine.length === 0
                    ? t(m.voluntaryNone)
                    : t(m.voluntarySummary, { works: countOf(mine.length, WORDS.work), origins: originSummaryLine(mine) })
                }
              >
                <Stack tight>
                  <p className="t-caption">{extraOutsideNote()}</p>
                  <DataTable
                    columns={extraColumns}
                    rows={extraRowsByDate(mine)}
                    getKey={(row) => row.id}
                    denominator={m.rowWord}
                    empty={{
                      title: t(m.voluntaryEmptyTitle),
                      reason: t(m.voluntaryEmptyReason),
                    }}
                  />
                </Stack>
              </Disclosure>
            );
          }}
        </DataGate>
      </Card>

      <Stack tight>
        <Disclosure label={t(m.contoursLabel)} summary={t(m.contoursSummary, { count: num(own.length), ha: ha(ownArea) })}>
          <DataGate res={contours} what={t(m.contoursGate)}>
            {() => (
              <DataTable
                columns={contourColumns}
                rows={own}
                getKey={(row) => row.id}
                denominator={m.contourWord}
                empty={{
                  title: t(m.contoursEmptyTitle),
                  reason: t(m.contoursEmptyReason),
                }}
              />
            )}
          </DataGate>
        </Disclosure>
      </Stack>

      <Card title={t(m.waterTitle)}>
        {waterLoading ? (
          <SkeletonList label={t(m.waterLoading)} items={2} tone="soft" />
        ) : waterMissing ? (
          <EmptyState
            compact
            title={t(m.waterNoneTitle)}
            reason={t(m.waterMissing, { reason: progress.reason ?? t(m.noReason) })}
          />
        ) : waterTotal === undefined ? (
          <EmptyState
            compact
            title={t(m.waterNoneTitle)}
            reason={t(m.waterAbsent)}
          />
        ) : (
          <>
            {/* Не «по циклам»: баланс посчитан по хозяйству целиком, потому
                что сезон и культура к воде не применяются. */}
            <ul className="facts">
              <li className="facts__row">
                <span className="facts__key">{t(m.waterRequests)}</span>
                <span className="facts__val">{num(waterTotal.requests)}</span>
              </li>
              <li className="facts__row">
                <span className="facts__key">{t(m.waterRequested)}</span>
                <span className="facts__val">{m3(waterTotal.requested_m3)}</span>
              </li>
              <li className="facts__row">
                <span className="facts__key">{t(m.waterDelivered)}</span>
                <span className="facts__val">{m3(waterTotal.delivered_m3)}</span>
              </li>
            </ul>
            <p className="t-caption">{t(m.waterNote)}</p>
            {waterNote ? <p className="t-caption">{waterNote}</p> : null}
          </>
        )}
      </Card>

      <Card size="lg" title={t(m.mapTitle)}>
        <div className="mapsplit">
          <div className="mapsplit__frame">
            <MapWidget
              colorBy="crop"
              crops={crops.data ?? undefined}
              mode="frame"
              height="100%"
              filterIds={ownIds.length > 0 ? ownIds : undefined}
              focusIds={ownIds.length > 0 ? ownIds : undefined}
              caption={t(m.mapCaption)}
            />
          </div>

          <div className="mapsplit__aside">
            <div className="mapaside__block">
              <div className="mapaside__title">{t(m.usageTitle)}</div>
              {byCategory.length === 0 ? (
                <div className="mapaside__note">
                  {contours.state === 'ready' ? t(m.usageNone) : t(m.usageNotRead)}
                </div>
              ) : (
                <div className="toplist toplist--nodot">
                  {byCategory.slice(0, DENSITY.listItems).map((row) => (
                    <div className="toplist__row" key={row.key}>
                      <span className="toplist__name">
                        {t(m.usageRow, { label: row.label, contours: countOf(row.contours, WORDS.contour) })}
                      </span>
                      <span className="toplist__val">{haUnit(row.area)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mapaside__block">
              <div className="mapaside__title">{t(m.placementTitle)}</div>
              <div className="mapaside__note">
                {contours.state === 'ready'
                  ? t(m.placementNote, { placed: num(placed), contours: t(m.contoursGenitive, { count: own.length }), geometry: geometryUnknown ? t(m.geometryUnknown) : t(m.geometryHave, { count: num(withGeometry) }) })
                  : t(m.placementLoading)}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Stack>
  );
}
